import { NovelSceneSchema, CoreEntityType, NovelActTree } from '#/index';
import { ListContainer, ExecuteParams, ExecutionContext, PromptFunction, TextContainer } from '../../types';
import { getNovelActTree, NovelSceneUtils } from '../../utils';
import { createCustomFunction } from './createFunction';
import { convertJSONContentToFormattedText } from '@/utils';

const getSceneByElements = (context: ExecutionContext, elements: ExecuteParams[]): NovelSceneSchema[] => {
  const scenes = new NovelSceneUtils(context);
  elements.forEach(element => {
    if (typeof element === 'object' && element.type === 'outlineTree') {
      scenes.byActsTree(element.data)
    } else if (typeof element === 'object' && element.type === CoreEntityType.Novels) {
      scenes.byNovel(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelActs) {
      scenes.byAct(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelChapters) {
      scenes.byChapter(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.NovelScenes) {
      scenes.byScene(element)
    } else if (typeof element === 'object' && element.type === CoreEntityType.CodexEntries) {
      scenes.byCodexEntry(element)
    }
    else if (typeof element === 'object' && element.type === CoreEntityType.CodexCategories) {
      scenes.byCodexCategory(element)
    } else if (typeof element === 'object' && element.type === 'codexDetail') {
      scenes.byCodexDetail(element)
    } else if (typeof element === 'object' && element.type === 'novelLabel') {
      scenes.byLabel(element)
    } else if (typeof element === 'object' && element.type === 'pointOfView') {
      scenes.byPov(element)
    } else if (typeof element === 'string') {
      scenes.byText(element)
    } else if (typeof element === 'object' && element.type === 'textContainer' && element.reference) {
      getSceneByElements(context, [element.reference]).forEach(scene => {
        scenes.byScene(scene)
      })
    } else if (typeof element === 'object' && element.type === 'listContainer' && element.elements) {
      getSceneByElements(context, element.elements).forEach(scene => {
        scenes.byScene(scene)
      })
    }
  })
  return scenes.getResult();
}

// 从参数中解析场景
const resolveScenesFromParams = (context: ExecutionContext, params: ExecuteParams[]): NovelSceneSchema[] => {
  if (!params.length) {
    const currentScene = context.globalContext.currentPosition?.scene;
    return currentScene ? [currentScene] : [];
  }
  return getSceneByElements(context, params);
};

// 获取场景序列信息
const getSceneSequenceInfo = (context: ExecutionContext) => {
  const { novelActsTree } = context.globalContext.generateStore.getState();
  const sequence: NovelActTree[] = [];
  const traverse = (node: NovelActTree) => {
    if (node.data.type === CoreEntityType.NovelScenes) {
      sequence.push(node);
    }
    node.children?.forEach(child => traverse(child));
  };
  novelActsTree.forEach(tree => traverse(tree));
  const indexMap = new Map<string, number>();
  sequence.forEach((node, index) => {
    indexMap.set(node.id, index);
  });
  return { sequence, indexMap };
};

// 添加唯一场景
const addUniqueScene = (list: NovelSceneSchema[], scene: NovelSceneSchema | undefined, seen: Set<string>) => {
  if (!scene || seen.has(scene.id)) return;
  seen.add(scene.id);
  list.push(scene);
};



/**
 * 场景相关函数
 * @returns 场景相关函数对象
 */
const scenesFunctions: Record<string, PromptFunction> = {
  isScene: createCustomFunction(
    'isScene',
    '从给定集合中过滤出真正的场景对象, 并返回完整结构化的XML。',
    (context, ...params: ExecuteParams[]): ListContainer<TextContainer | NovelSceneSchema> => {
      const textContainers: (TextContainer | NovelSceneSchema)[] = []
      const pushParam = (elements: ExecuteParams[]) => {
        elements.forEach(element => {
          if (typeof element === 'object' && element.type === 'listContainer' && element.elements) {
            pushParam(element.elements)
          } else if (typeof element === 'object' && element.type === 'textContainer' && element.reference) {
            textContainers.push(element.reference as NovelSceneSchema)
          } else if (typeof element === 'object' && element.type === CoreEntityType.NovelScenes) {
            textContainers.push(element as NovelSceneSchema)
          }
        })
      }
      pushParam(params)
      return {
        type: 'listContainer',
        elements: textContainers,
        defaultFormat: 'inherited',
      }
    }
  ),
  nextBeat: createCustomFunction(
    'nextBeat',
    '获取当前节之后紧接的下一节（在当前章内）。',
    (context): TextContainer | boolean => {
      const sceneId = context.globalContext.currentPosition?.scene?.id
      if (!sceneId) {
        return false
      }
      const chapter = context.globalContext.generateStore.getState().novelChapters.find(d => d.relationships.scenes.includes(sceneId))
      if (!chapter) return false;
      const sceneIndex = chapter.relationships.scenes.indexOf(sceneId)
      const nextId = chapter.relationships.scenes[sceneIndex + 1]
      if (nextId === undefined) return false;
      const nextScene = context.globalContext.generateStore.getState().novelScenes.find(d => d.id === nextId)
      if (!nextScene || !nextScene.attributes.content) return false;
      return {
        type: 'textContainer',
        reference: nextScene,
        defaultFormat: 'plain',
        formatters: {
          plain: (container) => convertJSONContentToFormattedText(container.reference as NovelSceneSchema),
        },
      }
    }
  ),
  previousBeat: createCustomFunction(
    'previousBeat',
    '获取当前节之前紧接的上一节（在当前章内）。',
    (context): TextContainer | boolean => {
      const sceneId = context.globalContext.currentPosition?.scene?.id
      if (!sceneId) {
        return false
      }
      const chapter = context.globalContext.generateStore.getState().novelChapters.find(d => d.relationships.scenes.includes(sceneId))
      if (!chapter) return false;
      const sceneIndex = chapter.relationships.scenes.indexOf(sceneId)
      const prevId = chapter.relationships.scenes[sceneIndex - 1]
      if (prevId === undefined) return false;
      const prevScene = context.globalContext.generateStore.getState().novelScenes.find(d => d.id === prevId)
      if (!prevScene || !prevScene.attributes.content) return false;
      return {
        type: 'textContainer',
        reference: prevScene,
        defaultFormat: 'plain',
        formatters: {
          plain: (container) => convertJSONContentToFormattedText(container.reference as NovelSceneSchema),
        },
      }
    }
  ),
  scene: createCustomFunction(
    'scene',
    '从给定参数中提取所有可能的场景对象。如果没有提供参数，则默认为当前节。',
    (context, ...params: ExecuteParams[]): ListContainer<NovelSceneSchema> => {
      let scenes: NovelSceneSchema[] = []
      if (!params.length && context.globalContext.currentPosition?.scene) {
        scenes.push(context.globalContext.currentPosition.scene)
      } else {
        scenes = getSceneByElements(context, params)
      }
      return {
        type: 'listContainer',
        elements: scenes,
        defaultFormat: 'inherited',
      }
    }
  ),
  'scene.fullText': createCustomFunction(
    'scene.fullText',
    'Returns the full text/prose for each possible "Scene" in the given arguments (or the current Scene if none are provided).',
    (context, ...params: ExecuteParams[]): ListContainer<NovelSceneSchema> => {
      let scenes: NovelSceneSchema[] = []
      if (!params.length && context.globalContext.currentPosition?.scene) {
        scenes.push(context.globalContext.currentPosition.scene)
      } else {
        scenes = getSceneByElements(context, params)
      }

      return {
        type: 'listContainer',
        elements: scenes,
        defaultFormat: 'inherited',
      }
    }
  ),
  'scene.fullTitle': createCustomFunction(
    'scene.fullTitle',
    'Extracts the full title of a Scene with chapter information for the given arguments.',
    (context, ...params: ExecuteParams[]): ListContainer<TextContainer> => {
      let scenes: NovelSceneSchema[] = []
      if (!params.length && context.globalContext.currentPosition?.scene) {
        scenes.push(context.globalContext.currentPosition.scene)
      }
      scenes = getSceneByElements(context, params)
      return {
        type: 'listContainer',
        elements: scenes.map(item => {
          const trees = getNovelActTree(context.globalContext.generateStore.getState().novelActsTree, { sceneIds: [item.id] })
          const chapterData = trees[0]?.children?.[0]
          const sceneData = chapterData?.children?.[0]
          if (!sceneData) return;
          return {
            type: 'textContainer',
            title: sceneData.fullName,
            reference: item,
            defaultFormat: 'plain',
            formatters: {
              plain: (container) => container.title,
            },
          } as TextContainer
        }).filter(d => d !== undefined),
        defaultFormat: 'inherited',
      }
    }
  ),
  'scene.hasLabel': createCustomFunction(
    'scene.hasLabel',
    '过滤出带有指定标签（label）的 Scene',
    () => ''
  ),
  'scene.hasMention': createCustomFunction(
    'scene.hasMention',
    '过滤出在文本中提及某个 Codex 条目的 Scene',
    () => ''
  ),
  'scene.hasReference': createCustomFunction(
    'scene.hasReference',
    '过滤出在引用（reference）中包含某个 Codex 条目的 Scene',
    () => ''
  ),
  'scene.hasSubtitle': createCustomFunction(
    'scene.hasSubtitle',
    '过滤出具有子标题（subtitle）的 Scene',
    () => ''
  ),
  'scene.hasSummary': createCustomFunction(
    'scene.hasSummary',
    '过滤出具有摘要（summary）的 Scene',
    () => ''
  ),
  'scene.hasText': createCustomFunction(
    'scene.hasText',
    '过滤出具有正文内容（text）的 Scene',
    () => ''
  ),
  'scene.labels': createCustomFunction(
    'scene.labels',
    '提取指定 Scene 的标签列表',
    () => ''
  ),
  'scene.next': createCustomFunction(
    'scene.next',
    '返回给定参数中每个可能的"节"（若未提供参数则指当前节）的紧接着的下一节。',
    (context, ...params: ExecuteParams[]): ListContainer<NovelSceneSchema> | void => {
      const baseScenes = resolveScenesFromParams(context, params);
      if (!baseScenes.length) return;

      const { sequence, indexMap } = getSceneSequenceInfo(context);
      const result: NovelSceneSchema[] = [];
      const seen = new Set<string>();

      baseScenes.forEach(scene => {
        const currentIndex = indexMap.get(scene.id);
        if (currentIndex === undefined) return;
        const nextNode = sequence[currentIndex + 1];
        if (!nextNode) return;
        addUniqueScene(result, nextNode.data as NovelSceneSchema, seen);
      });

      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    }
  ),
  'scene.nextSamePOV': createCustomFunction(
    'scene.nextSamePOV',
    '返回给定参数中每个可能的"节"（若未提供参数则指当前节）的、且共享相同视角的紧接着的下一节。',
    (context, ...params: ExecuteParams[]): ListContainer<NovelSceneSchema> | void => {
      const baseScenes = resolveScenesFromParams(context, params);
      if (!baseScenes.length) return;

      const { sequence, indexMap } = getSceneSequenceInfo(context);
      const result: NovelSceneSchema[] = [];
      const seen = new Set<string>();

      baseScenes.forEach(scene => {
        const currentIndex = indexMap.get(scene.id);
        const currentPov = scene.attributes.pov;
        if (currentIndex === undefined || !currentPov?.type) return;

        for (let i = currentIndex + 1; i < sequence.length; i++) {
          const candidate = sequence[i].data as NovelSceneSchema;
          const candidatePov = candidate.attributes.pov;
          if (
            candidatePov?.type === currentPov.type &&
            candidatePov?.characterId === currentPov.characterId
          ) {
            addUniqueScene(result, candidate, seen);
            break;
          }
        }
      });

      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    }
  ),
  'scene.number': createCustomFunction(
    'scene.number',
    '提取指定 Scene 的编号',
    () => ''
  ),
  'scene.previous': createCustomFunction(
    'scene.previous',
    '返回每个给定场景的紧邻上一节，如果没有提供参数，则返回当前作用域的上一节',
    (context, ...params: ExecuteParams[]): ListContainer<NovelSceneSchema> | void => {
      const baseScenes = resolveScenesFromParams(context, params);
      if (!baseScenes.length) return;

      const { sequence, indexMap } = getSceneSequenceInfo(context);
      const result: NovelSceneSchema[] = [];
      const seen = new Set<string>();

      baseScenes.forEach(scene => {
        const currentIndex = indexMap.get(scene.id);
        if (currentIndex === undefined || currentIndex === 0) return;
        const prevNode = sequence[currentIndex - 1];
        addUniqueScene(result, prevNode?.data as NovelSceneSchema, seen);
      });

      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    }
  ),
  'scene.previousSamePOV': createCustomFunction(
    'scene.previousSamePOV',
    '返回与给定参数中每个可能的"节"（若未提供参数则指当前节）共享相同视角的前一个"节"。',
    (context, ...params: ExecuteParams[]): ListContainer<NovelSceneSchema> | void => {
      const baseScenes = resolveScenesFromParams(context, params);
      if (!baseScenes.length) return;

      const { sequence, indexMap } = getSceneSequenceInfo(context);
      const result: NovelSceneSchema[] = [];
      const seen = new Set<string>();

      baseScenes.forEach(scene => {
        const currentIndex = indexMap.get(scene.id);
        const currentPov = scene.attributes.pov;
        if (currentIndex === undefined || !currentPov?.type) return;

        for (let i = currentIndex - 1; i >= 0; i--) {
          const candidate = sequence[i].data as NovelSceneSchema;
          const candidatePov = candidate.attributes.pov;
          if (
            candidatePov?.type === currentPov.type &&
            candidatePov?.characterId === currentPov.characterId
          ) {
            addUniqueScene(result, candidate, seen);
            break;
          }
        }
      });

      return {
        type: 'listContainer',
        elements: result,
        defaultFormat: 'inherited',
      }
    }
  ),
  'scene.references': createCustomFunction(
    'scene.references',
    '提取指定 Scene 所引用（references）的 Codex 条目列表',
    () => ''
  ),
  'scene.subtitle': createCustomFunction(
    'scene.subtitle',
    '提取指定 Scene 的子标题（若有）',
    () => ''
  ),
  'scene.summary': createCustomFunction(
    'scene.summary',
    '提取指定 Scene 的摘要（summary）',
    () => ''
  ),
  'scene.title': createCustomFunction(
    'scene.title',
    '提取指定 Scene 的标题（title）',
    () => ''
  ),
}

export default scenesFunctions;